import { useIntl } from "react-intl";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useScrollIntoView } from "@mantine/hooks";
import { useMantineTheme } from "@mantine/core";
import { getBreakpoint } from "@/utils/mantine";
import HydratedMediaQuery from "@/utils/hydratedMediaQuery";
import { CtaButtonWithLoader } from "@/components/common/ctaButtonWithLoader/CtaButtonWithLoader";
import { DoubleCheckmarkIcon } from "@/public/icons/DoubleCheckmarkIcon";
import { Select } from "@/components/common/select/Select";
import TrackingService from "@/utils/trackingService";
import { AgencyB2B } from "@/strapiQuery/agenciesB2B";
import CompanySearchInput from "@/components/common/companySearchInput/CompanySearchInput";
import { CompanyProposition } from "@/components/common/companySearchInput/hooks/useFetchAutocompleteCompanies";
import {
  ContactFormValues,
  sendContactFormValues,
} from "./helpers/sendContactForm";
import styles from "./ContactForm.module.scss";
import { sendContactDemand } from "./helpers/sendContactDemand";
import { FormPrivacyModal } from "../FormPrivacyModal/FormPrivacyModal";

type FormError = {
  id: string;
  label: string;
};

type ContactFormProps = {
  agencyName: string | undefined;
  agenciesB2B: AgencyB2B[];
  brochureLink?: string;
};

export const ContactForm = ({
  agencyName,
  agenciesB2B,
  brochureLink,
}: ContactFormProps) => {
  const intl = useIntl();

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const theme = useMantineTheme();
  const isTabletOrDesktop = HydratedMediaQuery(
    getBreakpoint(theme.breakpoints.md)
  );
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyProposition | null>(null);
  useEffect(() => {
    if (selectedCompany !== null) {
      setSocialReason(selectedCompany.businessName);
    }
  }, [selectedCompany]);
  // Scroll action
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 0,
    duration: 400,
  });

  agenciesB2B.sort((a, b) => {
    if (a.agencyName > b.agencyName) {
      return 1;
    } else if (a.agencyName < b.agencyName) {
      return -1;
    } else {
      return 0;
    }
  });

  const [preSelected, setPreSelected] = useState<number>(-1);
  const [agencySelected, setAgencySelected] = useState<AgencyB2B>();
  const [socialReason, setSocialReason] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [telephone, setTelephone] = useState<string>("");
  const [demand, setDemand] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<FormError[]>([]);
  const [isFormSent, setIsFormSent] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const validateForm = () => {
    const errors: FormError[] = [];

    if (!isCheckboxChecked) {
      errors.push({
        id: "checkbox",
        label: intl.formatMessage({
          id: "contactProCheckboxError",
        }),
      });
    }
    const emailRegExp = new RegExp(
      "^[a-zA-Z0-9]+(?:.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:.[a-zA-Z0-9]+)*$"
    );

    if (!agencySelected || agencySelected.agencyName.length === 0) {
      errors.push({ id: "agencySelected", label: "contactProAgencyMissing" });
    }
    if (socialReason.length === 0) {
      errors.push({ id: "socialReason", label: "contactProSocialMissing" });
    }
    if (lastname.length === 0) {
      errors.push({ id: "lastname", label: "contactProLastnameMissing" });
    }
    if (email.length === 0) {
      errors.push({ id: "email", label: "contactProEmailMissing" });
    } else if (!emailRegExp.test(email)) {
      errors.push({ id: "email", label: "contactProMalformedEmail" });
    }
    if (telephone.length === 0) {
      errors.push({ id: "telephone", label: "contactProTelephoneMissing" });
    } else if (
      !telephone.match(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/gim)
    ) {
      errors.push({ id: "telephone", label: "contactProMalformedTelephone" });
    }
    if (demand.length === 0) {
      errors.push({ id: "demand", label: "contactProDemandMissing" });
    }

    return errors;
  };

  const onInputChange = (id: string, callback: () => void) => {
    setErrorList(errorList.filter((value) => value.id !== id));

    callback();
  };
  const sendContactForm = (contactFormValues: ContactFormValues) => {
    sendContactFormValues(contactFormValues)
      .then((response) => {
        if (response.status === 200) {
          setIsLoading(false);
          setIsFormSent(true);
          TrackingService.trackLeadFormPro(contactFormValues.agencySelected);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsFormSent(true);
        setIsError(true);
      });
  };

  const submitHandler = async () => {
    const errors = validateForm();
    setErrorList(errors);
    setIsLoading(true);

    if (errors.length) {
      setIsLoading(false);

      return;
    } else {
      if (agencySelected !== undefined) {
        const contactFormValues: ContactFormValues = {
          agencySelected: agencySelected,
          socialReason: socialReason,
          lastname: lastname,
          email: email,
          telephone: telephone,
          demand: demand,
        };
        await sendContactDemand(contactFormValues);
        sendContactForm(contactFormValues);
      }
    }
  };

  useEffect(() => {
    if (agencyName !== undefined) {
      agenciesB2B.map((a, index) => {
        if (a.agencyName === agencyName) {
          setAgencySelected(a);
          setPreSelected(index);
        }
      });
      scrollIntoView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyName]);

  const handleModalClose = () => {
    setErrorList(errorList.filter((value) => value.id !== "checkbox"));
    setIsCheckboxChecked(true);
    if (checkboxRef.current) {
      checkboxRef.current.style.borderColor = "";
    }
  };
  const toggleCheckboxHandler = () => {
    setErrorList(errorList.filter((value) => value.id !== "checkbox"));
    setIsCheckboxChecked((prev) => !prev);
  };

  return (
    <div className={styles.container} ref={targetRef}>
      <div className={styles.form}>
        <div className={styles.title}>
          {intl.formatMessage({ id: "contactProTitle" })}
        </div>
        <div
          className={classNames(styles.select, {
            [styles.error]: !!errorList.find(
              (value) => value.id === "agencySelected"
            ),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "contactProAgency" })}*
          </div>
          <div className={styles.input}>
            <Select<AgencyB2B>
              initialIndex={preSelected}
              setSelectedValue={setAgencySelected}
              selectOptions={agenciesB2B}
              selectOptionsLabels={agenciesB2B.map((a) => a.agencyName)}
              groupBy={"department"}
              placeholder={intl.formatMessage({
                id: "contactProAgencyPlaceholder",
              })}
              isDisabled={isFormSent}
              isError={
                !!errorList.find((value) => value.id === "agencySelected")
              }
            />
          </div>
          {errorList.find((value) => value.id === "agencySelected") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "agencySelected")
                  ?.label,
              })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.text, {
            [styles.error]: !!errorList.find(
              (value) => value.id === "socialReason"
            ),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "contactProSocial" })}*
          </div>
          <CompanySearchInput
            placeholder={intl.formatMessage({
              id: "contactProSocialPlaceholder",
            })}
            onSelectCompany={setSelectedCompany}
            selectedCompany={selectedCompany}
          />
          {errorList.find((value) => value.id === "socialReason") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "socialReason")
                  ?.label,
              })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.text, {
            [styles.error]: !!errorList.find(
              (value) => value.id === "lastname"
            ),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "lastname" })}*
          </div>
          <div className={`${styles.input} ${styles.flex_center}`}>
            <input
              type="text"
              placeholder={intl.formatMessage({
                id: "contacProLastnamePlaceholder",
              })}
              value={lastname}
              disabled={isFormSent}
              onChange={(e) =>
                onInputChange("lastname", () => setLastname(e.target.value))
              }
            />
          </div>
          {errorList.find((value) => value.id === "lastname") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "lastname")?.label,
              })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.text, {
            [styles.error]: !!errorList.find((value) => value.id === "email"),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "email" })}*
          </div>
          <div className={`${styles.input} ${styles.flex_center}`}>
            <input
              type="email"
              placeholder={intl.formatMessage({
                id: "contactProEmailPlaceholder",
              })}
              value={email}
              disabled={isFormSent}
              onChange={(e) =>
                onInputChange("email", () => setEmail(e.target.value))
              }
            />
          </div>
          {errorList.find((value) => value.id === "email") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "email")?.label,
              })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.text, {
            [styles.error]: !!errorList.find(
              (value) => value.id === "telephone"
            ),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "telephone" })}*
          </div>
          <div className={`${styles.input} ${styles.flex_center}`}>
            <input
              type="tel"
              placeholder={intl.formatMessage({
                id: "contactProTelephonePlaceholder",
              })}
              pattern={"[0-9]{10}"}
              value={telephone}
              disabled={isFormSent}
              onChange={(e) =>
                onInputChange("telephone", () => setTelephone(e.target.value))
              }
            />
          </div>
          {errorList.find((value) => value.id === "telephone") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "telephone")?.label,
              })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.textArea, {
            [styles.error]: !!errorList.find((value) => value.id === "demand"),
          })}
        >
          <div className={styles.label}>
            {intl.formatMessage({ id: "contactProDemand" })}*
          </div>
          <div className={styles.subLabel}>
            {intl.formatMessage(
              { id: "contactProDemandDetails" },
              { br: () => <br /> }
            )}
          </div>
          <div className={styles.textInput}>
            <textarea
              placeholder={intl.formatMessage({ id: "demandPlaceholder" })}
              rows={5}
              value={demand}
              disabled={isFormSent}
              onChange={(e) =>
                onInputChange("demand", () => setDemand(e.target.value))
              }
              maxLength={1000}
            />
          </div>
          {errorList.find((value) => value.id === "demand") && (
            <div className={styles.inputError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "demand")?.label,
              })}
            </div>
          )}
        </div>
        <div className={styles.linkContainer}>
          {isTabletOrDesktop && brochureLink !== undefined && (
            <div className={styles.link}>
              {intl.formatMessage({ id: "downloadOurBrochure" })}
              <a href={brochureLink} target="_blank" rel="noreferrer">
                {intl.formatMessage({ id: "clickHere" })}
              </a>
            </div>
          )}

          {!isFormSent && (
            <div className={styles.confirmButton}>
              <CtaButtonWithLoader
                label={intl.formatMessage({ id: "contactProConfirm" })}
                isFullWidth={true}
                isLoading={isLoading}
                isMarineBg={true}
                onClick={() => submitHandler()}
              />
            </div>
          )}

          {isFormSent && !isError && (
            <div className={styles.confirmation}>
              <DoubleCheckmarkIcon color={"#37837d"} />
              {intl.formatMessage({ id: "contactProConfirmation" })}
            </div>
          )}
          {isFormSent && isError && (
            <div className={styles.confirmationError}>
              {intl.formatMessage({ id: "contactProErrorConfirmation" })}
            </div>
          )}
        </div>
        <div
          className={classNames(styles.checkboxContainer, {
            [styles.error]: !!errorList.find(
              (value) => value.id === "checkbox"
            ),
          })}
        >
          <div className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              id="termsCheckbox"
              checked={isCheckboxChecked}
              readOnly
              ref={checkboxRef}
              className={styles.checkbox}
              onClick={toggleCheckboxHandler}
            />
            <label
              htmlFor="termsCheckbox"
              onClick={() => setIsPrivacyModalOpen(true)}
              className={styles.checkboxLabel}
            >
              {intl.formatMessage({ id: "contactProPrivacyCheckboxLabel" })}
            </label>
          </div>
          {errorList.find((value) => value.id === "checkbox") && (
            <div className={styles.checkboxError}>
              {intl.formatMessage({
                id: errorList.find((value) => value.id === "checkbox")?.label,
              })}
            </div>
          )}
        </div>
      </div>
      <FormPrivacyModal
        isOpen={isPrivacyModalOpen}
        setIsOpen={setIsPrivacyModalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};
